/**
 * Till Payments TypeScript Type Definitions
 *
 * These types define the interface for Till Payments' Payment.js library
 * used for PCI-compliant card tokenization.
 *
 * @see https://gateway.tillpayments.com/documentation/gateway#paymentjs-javascript-integration
 */

/**
 * Supported input events for Till Payment.js iframe fields
 */
export type TillInputEvent =
  | 'input'
  | 'focus'
  | 'blur'
  | 'enter'
  | 'esc'
  | 'tab'
  | 'shift-tab'
  | 'mouseover'
  | 'mouseout';

/**
 * CSS styles that can be applied to Till iframe inputs.
 * Due to PCI restrictions, only inline styles work - no external fonts/images.
 * Supports any valid CSS property as key-value pairs.
 * @see https://gateway.tillpayments.com/documentation/gateway#paymentjs-javascript-integration
 *
 * @example
 * { "border": "1px solid red", "font-size": "12px", "color": "#000" }
 *
 * Placeholder styling uses special ::placeholder key:
 * { "::placeholder": { "color": "#a00" } }
 */
export type TillInputStyle = Record<string, string | number | Record<string, string>>;

/**
 * Default input style matching the app's design system
 */
export const TILL_INPUT_STYLE_DEFAULT: TillInputStyle = {
  width: '100%',
  height: 36,
  'font-weight': '400',
  'font-size': '16px',
  'border-radius': '0',
  'border-color': 'rgb(134, 147, 158)',
  'border-bottom-width': '1px',
  'border-top-width': '0',
  'border-left-width': '0',
  'border-right-width': '0',
  'padding-left': '4',
  'padding-right': '4',
};

/**
 * Data returned by Till iframe input event callbacks
 */
export interface TillCardInputData {
  /** Whether the card number passes Luhn check */
  validNumber: boolean;
  /** Whether CVV is valid length for card type */
  validCvv: boolean;
  /** Current length of entered card number */
  numberLength: number;
  /** Current length of entered CVV */
  cvvLength: number;
  /** Detected card type: 'visa', 'mastercard', 'amex', etc. */
  cardType: string;
  /** First 6 digits (BIN) for card identification */
  firstSix: string;
  /** Sub-brand if applicable (e.g., 'electron' for Visa) */
  subBrand: string | null;
}

/**
 * Data required for tokenization request.
 * MUST include either card_holder OR (first_name AND last_name), plus month and year.
 * @see https://gateway.tillpayments.com/documentation/gateway#paymentjs-javascript-integration
 */
export interface TillTokenizeData {
  /** Cardholder name as printed on card (alternative to first_name/last_name) */
  card_holder?: string;
  /** First name (alternative to card_holder, must be paired with last_name) */
  first_name?: string;
  /** Last name (alternative to card_holder, must be paired with first_name) */
  last_name?: string;
  /** Expiry month (01-12) */
  month: string;
  /** Expiry year (2-digit: '25' or 4-digit: '2025') */
  year: string;
  /** Optional: Email address */
  email?: string;
  /** Optional: Phone number */
  phone_number?: string;
  /** Optional: Company name */
  company?: string;
  /** Optional: Address line 1 */
  address1?: string;
  /** Optional: ZIP/Postal code */
  zip?: string;
  /** Optional: City */
  city?: string;
  /** Optional: State/Province */
  state?: string;
  /** Optional: Country code */
  country?: string;
}

/**
 * Error returned by Till tokenization
 */
export interface TillTokenizeError {
  /** Field that has the error: 'number', 'cvv', 'month', 'year', 'card_holder', 'integration_key' */
  attribute: string;
  /** Error code: 'errors.blank', 'errors.invalid', 'errors.expired', etc. */
  key: string;
  /** Human-readable error message */
  message: string;
}

/**
 * Card data returned on successful tokenization
 * @see https://gateway.tillpayments.com/documentation/gateway#paymentjs-javascript-integration
 */
export interface TillCardData {
  /** Card type: 'visa', 'mastercard', 'amex', 'diners', 'discover', 'jcb', 'maestro', 'uatp', 'unionpay', 'visa_electron' */
  card_type: string;
  /** Cardholder name */
  full_name: string;
  /** BIN digits (Bank Identification Number) - official field name */
  bin_digits: string;
  /** First 6 digits (BIN) - alias for bin_digits */
  first_six_digits: string;
  /** Last 4 digits */
  last_four_digits: string;
  /** Expiry month */
  month: string;
  /** Expiry year */
  year: string;
  /** Unique card fingerprint for identification */
  fingerprint: string;
}

/**
 * Till Payment instance returned by init() callback
 */
export interface TillPaymentInstance {
  /** Apply CSS styles to card number iframe */
  setNumberStyle(style: TillInputStyle): void;
  /** Apply CSS styles to CVV iframe */
  setCvvStyle(style: TillInputStyle): void;
  /** Attach event listener to card number iframe */
  numberOn(event: TillInputEvent, callback: (data: TillCardInputData) => void): void;
  /** Attach event listener to CVV iframe */
  cvvOn(event: TillInputEvent, callback: (data: TillCardInputData) => void): void;
}

/**
 * Main PaymentJs class interface
 * @see https://gateway.tillpayments.com/documentation/gateway#paymentjs-javascript-integration
 */
export interface TillPaymentJs {
  /** Initialize Till iframes for card input */
  init(
    integrationKey: string,
    numberDivId: string,
    cvvDivId: string,
    callback: (payment: TillPaymentInstance) => void,
  ): void;

  /** Tokenize entered card data */
  tokenize(
    data: TillTokenizeData,
    successCallback: (token: string, cardData: TillCardData) => void,
    errorCallback: (errors: TillTokenizeError[]) => void,
  ): void;

  /**
   * Initialize CVV refresh for stored card (card-on-file)
   * Used when re-collecting CVV for an existing tokenized card
   * @see https://gateway.tillpayments.com/documentation/gateway#cvv-only-tokenization
   */
  initCvvRefresh?(
    integrationKey: string,
    referenceTransactionId: string,
    cvvDivId: string,
    callback: (payment: TillPaymentInstance) => void,
  ): void;

  /** Refresh CVV without full re-tokenization (call after initCvvRefresh) */
  refreshCvv?(
    successCallback: () => void,
    errorCallback: (errors: TillTokenizeError[]) => void,
  ): void;

  // Additional helper methods (optional)

  /** Set placeholder text for card number input */
  setNumberPlaceholder?(text: string): void;

  /** Set placeholder text for CVV input */
  setCvvPlaceholder?(text: string): void;

  /** Disable card holder requirement (default: required) */
  setRequireCardHolder?(required: boolean): void;

  /** Enable browser/password manager autofill support */
  enableAutofill?(): void;

  /** Handle autofilled data from browser */
  onAutofill?(callback: (data: Partial<TillTokenizeData>) => void): void;
}

/**
 * Global PaymentJs constructor (loaded via script tag)
 */
declare global {
  class PaymentJs implements TillPaymentJs {
    init(
      integrationKey: string,
      numberDivId: string,
      cvvDivId: string,
      callback: (payment: TillPaymentInstance) => void,
    ): void;
    tokenize(
      data: TillTokenizeData,
      successCallback: (token: string, cardData: TillCardData) => void,
      errorCallback: (errors: TillTokenizeError[]) => void,
    ): void;
    initCvvRefresh?(
      integrationKey: string,
      referenceTransactionId: string,
      cvvDivId: string,
      callback: (payment: TillPaymentInstance) => void,
    ): void;
    refreshCvv?(
      successCallback: () => void,
      errorCallback: (errors: TillTokenizeError[]) => void,
    ): void;
    setNumberPlaceholder?(text: string): void;
    setCvvPlaceholder?(text: string): void;
    setRequireCardHolder?(required: boolean): void;
    enableAutofill?(): void;
    onAutofill?(callback: (data: Partial<TillTokenizeData>) => void): void;
  }
}
